/**
 * 配置块导入导出组件
 * 支持 JSON 导入导出和 OSS 导出功能
 */
import React, { useState } from 'react'
import {
  Button,
  Space,
  Upload,
  Modal,
  Form,
  Select,
  Input,
  Tag,
  Typography,
  message,
  Alert,
  Divider,
  Steps,
  Result,
  Table,
} from 'antd'
import {
  ImportOutlined,
  ExportOutlined,
  UploadOutlined,
  DownloadOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons'
import { configBlockApi } from '@/services/api'
import { CONFIG_TYPE_LABELS, ConfigType } from '@/types/scriptConfigBlock'
import { validateBatch } from '@/utils/schemaValidator'

const { Text, Paragraph } = Typography
const { TextArea } = Input

// 导入预览表格列
const previewColumns = [
  {
    title: '配置键',
    dataIndex: 'config_key',
    key: 'config_key',
    width: 150,
    render: (text: string) => <Text code>{text}</Text>
  },
  {
    title: '配置名称',
    dataIndex: 'config_name',
    key: 'config_name',
    width: 200,
  },
  {
    title: '类型',
    dataIndex: 'config_type',
    key: 'config_type',
    width: 100,
    render: (type: ConfigType) => (
      <Tag>{CONFIG_TYPE_LABELS[type] || type}</Tag>
    )
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: (status: string) => (
      <Tag color={status === 'valid' ? 'green' : 'red'}>
        {status === 'valid' ? '有效' : '无效'}
      </Tag>
    )
  },
  {
    title: '错误信息',
    dataIndex: 'error',
    key: 'error',
    render: (error: string) => error ? <Text type="danger">{error}</Text> : '-'
  }
]

// 导入预览项
interface ImportPreviewItem {
  config_key: string
  config_name: string
  config_type: ConfigType
  config_data: Record<string, any>
  sort_order?: number
  status: 'valid' | 'invalid'
  error?: string
}

// 导入组件
const ConfigBlockImport: React.FC<{
  packageId: string
  onImportComplete: () => void
}> = ({ packageId, onImportComplete }) => {
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [jsonInput, setJsonInput] = useState('')
  const [preview, setPreview] = useState<ImportPreviewItem[]>([])
  const [importing, setImporting] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  // 验证 JSON 格式
  const validateAndPreview = () => {
    try {
      const parsed = JSON.parse(jsonInput)
      const items = Array.isArray(parsed) ? parsed : [parsed]

      // 使用批量验证
      const validationResult = validateBatch(
        items.map(item => ({
          config_type: item.config_type,
          config_key: item.config_key,
          config_data: item.config_data || {}
        }))
      )

      const previewItems: ImportPreviewItem[] = items.map((item, index) => {
        const resultItem = validationResult.results.find(r => r.index === index)

        // 基础字段验证
        if (!item.config_key || !item.config_type || !item.config_name) {
          return {
            config_key: item.config_key || `item_${index}`,
            config_name: item.config_name || '未知',
            config_type: (item.config_type as ConfigType) || 'npc',
            config_data: item.config_data || {},
            status: 'invalid',
            error: '缺少必要字段 (config_key, config_type, config_name)'
          }
        }

        // Schema 验证结果
        if (resultItem && !resultItem.valid) {
          const errorMessages = resultItem.errors
            ?.map(e => `${e.field}: ${e.message}`)
            .join('; ')
          return {
            ...item,
            status: 'invalid',
            error: errorMessages || '数据验证失败'
          }
        }

        // 类型验证
        if (!['npc', 'quest', 'item', 'variable', 'timeline', 'world', 'faction', 'location', 'action', 'event', 'dialogue', 'ai_model'].includes(item.config_type)) {
          return {
            ...item,
            status: 'invalid',
            error: `无效的配置类型: ${item.config_type}`
          }
        }

        return {
          ...item,
          status: 'valid',
          error: undefined
        }
      })

      setPreview(previewItems)
      setCurrentStep(1)

      const validCount = previewItems.filter(i => i.status === 'valid').length
      const invalidCount = previewItems.length - validCount

      if (invalidCount > 0) {
        message.warning(`解析完成：${validCount} 条有效，${invalidCount} 条无效`)
      } else {
        message.success(`解析成功，共 ${items.length} 条配置，全部验证通过`)
      }
    } catch (e: any) {
      message.error(`JSON 解析失败: ${e.message}`)
    }
  }

  // 执行导入
  const executeImport = async () => {
    const validItems = preview.filter(item => item.status === 'valid')

    if (validItems.length === 0) {
      message.warning('没有有效的配置项可导入')
      return
    }

    setImporting(true)
    try {
      await configBlockApi.importBlocks(packageId, validItems.map(item => ({
        config_type: item.config_type,
        config_key: item.config_key,
        config_name: item.config_name,
        config_data: item.config_data,
        sort_order: item.sort_order || 0
      })))

      message.success(`成功导入 ${validItems.length} 条配置`)
      setImportModalVisible(false)
      setJsonInput('')
      setPreview([])
      setCurrentStep(0)
      onImportComplete()
    } catch (error: any) {
      message.error(error?.message || '导入失败')
    } finally {
      setImporting(false)
    }
  }

  // 模板 JSON
  const templateJson = JSON.stringify([
    {
      "config_type": "npc",
      "config_key": "mysterious_stranger",
      "config_name": "神秘陌生人",
      "config_data": {
        "key": "mysterious_stranger",
        "name": "神秘陌生人",
        "role": "关键证人",
        "personality": "沉默寡言",
        "background": "他在镇子上已经居住了很多年..."
      }
    },
    {
      "config_type": "quest",
      "config_key": "investigate_mystery",
      "config_name": "调查神秘事件",
      "config_data": {
        "key": "investigate_mystery",
        "name": "调查神秘事件",
        "quest_type": "main",
        "objectives": [
          { "id": "1", "description": "找到神秘陌生人", "type": "talk", "required_count": 1 }
        ],
        "rewards": []
      }
    }
  ], null, 2)

  return (
    <>
      <Button icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}>
        导入配置
      </Button>

      <Modal
        title="导入配置块"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false)
          setJsonInput('')
          setPreview([])
          setCurrentStep(0)
        }}
        width={900}
        footer={null}
      >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Steps.Step title="输入 JSON" description="粘贴或上传 JSON 数据" />
          <Steps.Step title="预览确认" description="验证数据格式" />
          <Steps.Step title="导入完成" description="确认导入" />
        </Steps>

        {currentStep === 0 && (
          <div>
            <Alert
              message="导入格式说明"
              description={
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  <li>支持单个对象或对象数组格式</li>
                  <li>必须包含 <Text code>config_type</Text>, <Text code>config_key</Text>, <Text code>config_name</Text>, <Text code>config_data</Text> 字段</li>
                  <li>config_type 必须是有效的配置类型</li>
                </ul>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />

            <Form.Item label="JSON 数据">
              <TextArea
                rows={12}
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder="请粘贴 JSON 数据..."
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={() => setJsonInput(templateJson)}>
                填入示例模板
              </Button>
              <Space>
                <Upload
                  accept=".json"
                  beforeUpload={(file) => {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                      setJsonInput(e.target?.result as string)
                    }
                    reader.readAsText(file)
                    return false
                  }}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>上传 JSON 文件</Button>
                </Upload>
                <Button type="primary" onClick={validateAndPreview}>
                  下一步：预览
                </Button>
              </Space>
            </Space>
          </div>
        )}

        {currentStep === 1 && (
          <div>
            <Alert
              message={`预览结果：${preview.filter(p => p.status === 'valid').length} 条有效，${preview.filter(p => p.status === 'invalid').length} 条无效`}
              type={preview.some(p => p.status === 'invalid') ? 'warning' : 'success'}
              style={{ marginBottom: 16 }}
            />

            <Table
              dataSource={preview}
              columns={previewColumns}
              rowKey="config_key"
              size="small"
              pagination={{
                pageSize: 5,
                total: preview.length,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
              }}
              style={{ marginBottom: 16 }}
            />

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={() => setCurrentStep(0)}>上一步</Button>
              <Button
                type="primary"
                onClick={executeImport}
                loading={importing}
                disabled={preview.every(p => p.status === 'invalid')}
              >
                确认导入 ({preview.filter(p => p.status === 'valid').length} 条)
              </Button>
            </Space>
          </div>
        )}
      </Modal>
    </>
  )
}

// 导出组件
const ConfigBlockExport: React.FC<{
  packageId: string
  packageName?: string
}> = ({ packageId, packageName }) => {
  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [configType, setConfigType] = useState<ConfigType | undefined>()
  const [exporting, setExporting] = useState(false)
  const [exportResult, setExportResult] = useState<{
    success: boolean
    storagePath?: string
    manifest?: any
    error?: string
  } | null>(null)

  // 导出到 OSS (打包为 ZIP 上传到 OSS)
  const handleExport = async () => {
    setExporting(true)
    setExportResult(null)

    try {
      const response = await configBlockApi.exportPackageToMinio(packageId)

      if (response.data?.export) {
        setExportResult({
          success: true,
          storagePath: response.data.export.minio_path,
          manifest: {
            version: response.data.version,
            block_count: response.data.local?.id ? 1 : 0,
            content_hash: response.data.export.file_hash
          }
        })
        message.success('导出成功！')
      }
    } catch (error: any) {
      setExportResult({
        success: false,
        error: error?.message || '导出失败'
      })
      message.error(error?.message || '导出失败')
    } finally {
      setExporting(false)
    }
  }

  // 下载导出文件
  const handleDownload = async () => {
    try {
      const response = await configBlockApi.exportBlocks(packageId, configType)

      if (response.data) {
        // 构建 JSON 内容
        const exportData = {
          package_id: packageId,
          exported_at: new Date().toISOString(),
          config_type_filter: configType || 'all',
          manifest: response.data.manifest,
          blocks: response.data.blocks || []
        }

        // 创建下载
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${packageId}_config_${configType || 'all'}_${Date.now()}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        message.success('下载成功')
      }
    } catch (error: any) {
      message.error('下载失败')
    }
  }

  return (
    <>
      <Button
        icon={<ExportOutlined />}
        onClick={() => setExportModalVisible(true)}
      >
        导出配置
      </Button>

      <Modal
        title={
          <Space>
            <CloudUploadOutlined />
            <span>导出配置块</span>
            {packageName && <Tag>{packageName}</Tag>}
          </Space>
        }
        open={exportModalVisible}
        onCancel={() => {
          setExportModalVisible(false)
          setExportResult(null)
        }}
        footer={null}
        width={600}
      >
        {!exportResult ? (
          <div>
            <Alert
              message="导出说明"
              description={
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  <li><Text strong>导出到 OSS</Text>：将配置导出到 OSS 存储，供 ai-engine 下载使用</li>
                  <li><Text strong>下载 JSON</Text>：将配置导出为 JSON 文件用于备份或手动部署</li>
                  <li>可选导出特定类型的配置，或导出全部配置</li>
                </ul>
              }
              type="info"
              style={{ marginBottom: 24 }}
            />

            <Form.Item label="配置类型筛选">
              <Select
                value={configType}
                onChange={setConfigType}
                placeholder="全部类型"
                allowClear
                style={{ width: '100%' }}
              >
                {Object.entries(CONFIG_TYPE_LABELS).map(([key, label]) => (
                  <Select.Option key={key} value={key}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Divider />

            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleDownload} icon={<DownloadOutlined />}>
                下载 JSON
              </Button>
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={handleExport}
                loading={exporting}
              >
                导出到 OSS
              </Button>
            </Space>
          </div>
        ) : exportResult.success ? (
          <Result
            status="success"
            title="导出成功"
            subTitle={
              <div>
                <Paragraph>
                  <Text strong>OSS 路径：</Text>
                  <Text code>{exportResult.storagePath}</Text>
                </Paragraph>
                {exportResult.manifest && (
                  <div>
                    <Text strong>Manifest 信息：</Text>
                    <ul style={{ marginTop: 8 }}>
                      <li>版本: {exportResult.manifest.version}</li>
                      <li>配置块数: {exportResult.manifest.block_count}</li>
                      <li>内容哈希: <Text code style={{ fontSize: 10 }}>{exportResult.manifest.content_hash?.substring(0, 16)}...</Text></li>
                    </ul>
                  </div>
                )}
              </div>
            }
            extra={[
              <Button
                key="close"
                onClick={() => {
                  setExportModalVisible(false)
                  setExportResult(null)
                }}
              >
                关闭
              </Button>
            ]}
          />
        ) : (
          <Result
            status="error"
            title="导出失败"
            subTitle={exportResult.error}
            extra={[
              <Button
                key="retry"
                type="primary"
                onClick={() => setExportResult(null)}
              >
                重试
              </Button>,
              <Button
                key="close"
                onClick={() => setExportModalVisible(false)}
              >
                关闭
              </Button>
            ]}
          />
        )}
      </Modal>
    </>
  )
}

// 导入导出工具栏组件
const ConfigBlockToolbar: React.FC<{
  packageId: string
  packageName?: string
  onRefresh: () => void
}> = ({ packageId, packageName, onRefresh }) => {
  return (
    <Space style={{ marginBottom: 16 }}>
      <ConfigBlockImport packageId={packageId} onImportComplete={onRefresh} />
      <ConfigBlockExport packageId={packageId} packageName={packageName} />
      <Divider type="vertical" />
      <Button icon={<DownloadOutlined />} onClick={onRefresh}>
        刷新
      </Button>
    </Space>
  )
}

export { ConfigBlockImport, ConfigBlockExport, ConfigBlockToolbar }
export default ConfigBlockToolbar
